import React, { useEffect, useRef } from 'react';
import { Button, Modal } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import generateTestId from 'packages/helpers/testIdHelpers';
import { DetachModalProps } from '../../types';
import { HiddenInput } from '../../ContentProperty.styled';
import modalContainer from 'packages/common/modalContainer';
import { useDeferredClose } from '~/hooks/useDeferredClose';

const DetachModal = ({ isOpen, onClose, onDetach, selectedCount }: DetachModalProps) => {
    const [shouldRender, onCloseAnimationEnd] = useDeferredClose(isOpen);
    const { translate } = useTranslate();

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleDetach = () => {
        onDetach();
        onClose();
    };

    const onKeyEnter = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleDetach();
        }
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <Modal
            container={modalContainer}
            isClosedOnOverlayClick
            title={translate('editor.sidebar.shots.contentProperty.detatch.modal.title', { count: selectedCount })}
            onClose={onClose}
            isOpen={isOpen}
            onCloseAnimationEnd={onCloseAnimationEnd}
            actionSecondary={
                <Button
                    variant="secondary"
                    onClick={onClose}
                    {...generateTestId('editor : modal : content properties detach cancel button')}
                    {...{ id: 'shots_content_property_detatch_modal_cancel' }}
                >
                    {translate('editor.sidebar.shots.contentProperties.modal.cancel')}
                </Button>
            }
            actionPrimary={
                <Button
                    variant="primary"
                    onClick={handleDetach}
                    {...generateTestId('editor : modal : content properties detach action button')}
                    {...{ id: 'shots_content_property_detatch_modal_action' }}
                >
                    {translate('editor.sidebar.shots.contentProperty.detatch.modal.detach')}
                </Button>
            }
        >
            {translate('editor.sidebar.shots.contentProperty.detatch.modal.desc')}
            <HiddenInput ref={inputRef} onKeyDown={onKeyEnter} onClick={(e) => e.stopPropagation()} />
        </Modal>
    );
};

export default React.memo(DetachModal);
